import React, { Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import styles from './Auth.module.scss';
import { apiService } from '../../services/api';
import { firebaseAuthService } from '../../services/firebaseAuth';
import OTPInput from '../../components/molecules/OTPInput/OTPInput';
import GoogleSignIn from '../../components/Auth/GoogleSignIn';

interface SignupState {
  authMethod: 'email' | 'phone';
  formData: {
    email: string;
    phone: string;
    password: string;
    confirmPassword: string;
    otp: string;
    agreeToTerms: boolean;
  };
  errors: {[key: string]: string};
  isLoading: boolean;
  otpSent: boolean;
  countdown: number;
  otpComplete: boolean;
  currentOtp: string;
}

// Higher-order component to provide navigation
function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    return <WrappedComponent {...props} navigate={navigate} />;
  };
}

class Signup extends Component<any, SignupState> {
  private countdownInterval: NodeJS.Timeout | null = null;

  constructor(props: any) {
    super(props);
    this.state = {
      authMethod: 'email',
      formData: {
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        otp: '',
        agreeToTerms: false
      },
      errors: {},
      isLoading: false,
      otpSent: false,
      countdown: 0,
      otpComplete: false,
      currentOtp: ''
    };
  }

  componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    firebaseAuthService.cleanup();
  }

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    this.setState(prevState => {
      const newFormData = {
        ...prevState.formData,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Real-time password matching validation
      const newErrors = { ...prevState.errors };
      if (name === 'confirmPassword' && newFormData.password && value) {
        if (newFormData.password !== value) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          delete newErrors.confirmPassword;
        }
      } else if (name === 'password' && prevState.formData.confirmPassword) {
        // If password changes and confirmPassword is already filled, re-validate
        if (value !== prevState.formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          delete newErrors.confirmPassword;
        }
      } else if (name !== 'confirmPassword') {
        // Clear error for other fields when user starts typing
        delete newErrors[name];
      }
      
      return {
        formData: newFormData,
        errors: newErrors
      };
    });
  };

  handleAuthMethodChange = (method: 'email' | 'phone') => {
    this.setState({
      authMethod: method,
      formData: {
        ...this.state.formData,
        phone: method === 'phone' ? this.state.formData.phone : '',
        otp: '',
        email: method === 'email' ? this.state.formData.email : '',
        password: method === 'email' ? this.state.formData.password : '',
        confirmPassword: method === 'email' ? this.state.formData.confirmPassword : ''
      },
      errors: {},
      otpSent: false,
      countdown: 0,
      otpComplete: false,
      currentOtp: ''
    });
    firebaseAuthService.cleanup();
  };

  handleSendOTP = async () => {
    const { phone, password, confirmPassword } = this.state.formData;
    
    // Validate phone number
    if (!phone) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, phone: 'Phone number is required' }
      }));
      return;
    }

    // Validate phone number format
    if (!/^\+?[\d\s\-\(\)]{10,}$/.test(phone)) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, phone: 'Please enter a valid phone number (e.g., +1234567890)' }
      }));
      return;
    }

    // Validate password
    if (!password) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, password: 'Password is required' }
      }));
      return;
    } else if (password.length < 8) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, password: 'Password must be at least 8 characters' }
      }));
      return;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, password: 'Password must contain uppercase, lowercase, and number' }
      }));
      return;
    }

    // Validate confirm password
    if (!confirmPassword) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, confirmPassword: 'Please confirm your password' }
      }));
      return;
    } else if (password !== confirmPassword) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, confirmPassword: 'Passwords do not match' }
      }));
      return;
    }

    this.setState({ isLoading: true, errors: {} });

    try {
      // Ensure reCAPTCHA container exists (create if doesn't exist)
      let container = document.getElementById('recaptcha-container-signup');
      if (!container) {
        container = document.createElement('div');
        container.id = 'recaptcha-container-signup';
        container.style.display = 'none';
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        document.body.appendChild(container);
      }

      // Wait a moment for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      await firebaseAuthService.initializeRecaptcha('recaptcha-container-signup');
      
      const result = await firebaseAuthService.sendOTP(phone);
      
      if (result.success) {
      this.setState({ 
        otpSent: true, 
        countdown: 60,
        errors: {},
        otpComplete: false,
        currentOtp: ''
      });
        
        // Start countdown timer
        this.countdownInterval = setInterval(() => {
          this.setState(prevState => {
            if (prevState.countdown <= 1) {
              if (this.countdownInterval) {
                clearInterval(this.countdownInterval);
              }
              return { countdown: 0 };
            }
            return { countdown: prevState.countdown - 1 };
          });
        }, 1000);
      } else {
        this.setState(prevState => ({
          errors: { ...prevState.errors, phone: result.message }
        }));
      }
    } catch (error: any) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, phone: error.message || 'Failed to send OTP' }
      }));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleOTPChange = (otp: string) => {
    // Track OTP changes for manual submit button
    this.setState({
      currentOtp: otp,
      otpComplete: otp.length === 6
    });
  };

  handleOTPComplete = async (otp: string) => {
    // Auto-submit when all digits are entered
    await this.verifyOTP(otp);
  };

  verifyOTP = async (otp?: string) => {
    const otpToVerify = otp || this.state.currentOtp;
    
    if (otpToVerify.length !== 6) {
      this.setState(prevState => ({
        errors: { ...prevState.errors, otp: 'Please enter all 6 digits' }
      }));
      return;
    }

    this.setState({ isLoading: true, errors: {} });

    try {
      // Verify OTP with Firebase
      const result = await firebaseAuthService.verifyOTP(otpToVerify);
      
      if (result.success && result.user) {
        // Get Firebase ID token
        const idToken = await firebaseAuthService.getIdToken();
        
        if (!idToken) {
          throw new Error('Failed to get Firebase authentication token');
        }

        const phoneNumber = result.user.phoneNumber || this.state.formData.phone;
        
        // For phone signup with password, register/login with phone + password
        // First try to login (user might already exist)
        try {
          const loginResponse = await apiService.login({
            identifier: phoneNumber,
            identifierType: 'phone',
            password: this.state.formData.password
          });
          
          if (loginResponse.success && loginResponse.data?.accessToken) {
            // User exists, login successful
            apiService.setTokens(
              loginResponse.data.accessToken,
              loginResponse.data.refreshToken
            );
            this.setState({ errors: {} });
            
            // Verify profile after successful mobile verification
            let isProfileComplete = loginResponse.data.user?.isProfileComplete ?? false;
            try {
              const verifyResponse = await apiService.verifyCustomerProfile();
              if (verifyResponse.success && verifyResponse.data) {
                isProfileComplete = verifyResponse.data.isProfileComplete ?? isProfileComplete;
                console.log('Profile verified successfully after mobile verification');
              }
            } catch (verifyError: any) {
              console.error('Profile verification failed:', verifyError);
              // Continue with login even if verification fails, use original isProfileComplete
            }
            
            // If profile is complete, go to dashboard; otherwise, go to profile page
            if (isProfileComplete === true) {
              console.log('Profile is complete, navigating to dashboard...');
              this.props.navigate('/customer/friends');
            } else {
              console.log('Profile is incomplete, navigating to profile page...');
              this.props.navigate('/profile');
            }
            return;
          }
        } catch (loginError: any) {
          // If login fails (user doesn't exist), proceed with registration
          console.log('Login failed, user does not exist, proceeding with registration');
        }
        
        // User doesn't exist, register with phone + password
        try {
          const registerResponse = await apiService.register({
            identifier: phoneNumber,
            identifierType: 'phone',
            password: this.state.formData.password,
            confirmPassword: this.state.formData.confirmPassword,
            firstName: '',
            lastName: '',
            email: '',
            phone: phoneNumber,
            dateOfBirth: ''
          });
          
          if (registerResponse.success) {
            // After registration, login with phone + password
            const loginResponse = await apiService.login({
              identifier: phoneNumber,
              identifierType: 'phone',
              password: this.state.formData.password
            });
            
            if (loginResponse.success && loginResponse.data?.accessToken) {
              apiService.setTokens(
                loginResponse.data.accessToken,
                loginResponse.data.refreshToken
              );
              this.setState({ errors: {} });
              
              // Verify profile after successful mobile verification
              let isProfileComplete = loginResponse.data.user?.isProfileComplete ?? false;
              try {
                const verifyResponse = await apiService.verifyCustomerProfile();
                if (verifyResponse.success && verifyResponse.data) {
                  isProfileComplete = verifyResponse.data.isProfileComplete ?? isProfileComplete;
                  console.log('Profile verified successfully after mobile verification');
                }
              } catch (verifyError: any) {
                console.error('Profile verification failed:', verifyError);
                // Continue with signup even if verification fails, use original isProfileComplete
              }
              
              // If profile is complete, go to dashboard; otherwise, go to profile page
              if (isProfileComplete === true) {
                console.log('Profile is complete, navigating to dashboard...');
                this.props.navigate('/customer/friends');
              } else {
                console.log('Profile is incomplete, navigating to profile page...');
                this.props.navigate('/profile');
              }
              return;
            } else {
              throw new Error('Registration successful but login failed');
            }
          } else {
            throw new Error(registerResponse.message || 'Registration failed');
          }
        } catch (registerError: any) {
          console.error('Registration failed:', registerError);
          
          // Check if it's a network error (backend not running)
          if (registerError.message.includes('Unable to connect to server')) {
            // For testing purposes, allow signup when backend is down
            console.log('Backend not available, using test mode');
            apiService.setTokens('test-access-token', 'test-refresh-token');
            this.setState({ errors: {} });
            this.props.navigate('/profile');
            return;
          }
          
          throw new Error(registerError.message || 'Registration failed');
        }
      } else {
        this.setState(prevState => ({
          errors: { ...prevState.errors, otp: result.message }
        }));
      }
    } catch (error: any) {
      console.error('OTP verification failed:', error);
      this.setState(prevState => ({
        errors: { ...prevState.errors, otp: error.message || 'Failed to verify OTP' }
      }));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (this.state.authMethod === 'email') {
      if (!this.state.formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(this.state.formData.email)) {
        newErrors.email = 'Email is invalid';
      }
    } else {
      if (!this.state.formData.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\+?[\d\s\-\(\)]{10,}$/.test(this.state.formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number (e.g., +1234567890)';
      }
    }

    if (!this.state.formData.password) {
      newErrors.password = 'Password is required';
    } else if (this.state.formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(this.state.formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!this.state.formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (this.state.formData.password !== this.state.formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!this.state.formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    this.setState({ errors: newErrors });
    return Object.keys(newErrors).length === 0;
  };

  handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!this.validateForm()) return;

    this.setState({ isLoading: true });
    
    try {
      console.log('Attempting registration with:', {
        identifier: this.state.authMethod === 'email' ? this.state.formData.email : this.state.formData.phone,
        identifierType: this.state.authMethod
      });
      
      // Call the real registration API - only identifier and password
      // Backend will check if user exists, if not create new user
      const response = await apiService.register({
        identifier: this.state.authMethod === 'email' ? this.state.formData.email : this.state.formData.phone,
        identifierType: this.state.authMethod,
        password: this.state.formData.password,
        confirmPassword: this.state.formData.confirmPassword,
        firstName: '', // Will be filled in profile update
        lastName: '', // Will be filled in profile update
        email: this.state.authMethod === 'email' ? this.state.formData.email : '',
        phone: this.state.authMethod === 'phone' ? this.state.formData.phone : '',
        dateOfBirth: '' // Will be filled in profile update
      });
      
      console.log('Registration API Response:', response);
      
      if (response.success && response.data) {
        // Handle successful registration
        console.log('Registration successful:', response.data.user);
        
        // Clear any previous errors
        this.setState({ errors: {} });
        
        // Navigate to profile page for onboarding
        console.log('Navigating to profile for onboarding...');
        this.props.navigate('/profile');
        console.log('Navigation called');
        
      } else {
        throw new Error(response.message || 'Registration failed');
      }
      
    } catch (error: any) {
      console.error('Registration failed:', error);
      
      // Check if it's a network error (backend not running)
      if (error.message.includes('Unable to connect to server')) {
        // For testing purposes, allow registration when backend is down
        console.log('Backend not available, using test mode for registration');
        
        // Clear any previous errors
        this.setState({ errors: {} });
        
        // Navigate to profile for testing
        this.props.navigate('/profile');
        return;
      }
      
      // Show error message to user
      this.setState({
        errors: {
          ...this.state.errors,
          general: error.message || 'Registration failed. Please check your information and try again.'
        }
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleGoogleSuccess = async (credentialResponse: any) => {
    const credential = credentialResponse.credential; // This is the JWT token
    
    // Optional: Decode to see user info (do NOT trust this fully – verify on backend!)
    try {
      const userInfo = jwtDecode(credential) as any;
      console.log('Google user info:', userInfo); // { email, name, picture, sub (Google ID), etc. }
    } catch (err) {
      console.error('Failed to decode JWT:', err);
    }

    this.setState({ isLoading: true });

    try {
      // Send credential to your Node.js backend for verification and login/signup
      // The backend will handle creating a new account if the user doesn't exist
      const response = await apiService.googleAuth(credential);
      
      if (response.success && response.data) {
        // Backend returns your app's JWT or session
        if (response.data.accessToken) {
          apiService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
          
          // Clear any previous errors
          this.setState({ errors: {} });
          
          // Verify profile after successful Gmail verification
          let isProfileComplete = response.data.user?.isProfileComplete ?? false;
          try {
            const verifyResponse = await apiService.verifyCustomerProfile();
            if (verifyResponse.success && verifyResponse.data) {
              isProfileComplete = verifyResponse.data.isProfileComplete ?? isProfileComplete;
              console.log('Profile verified successfully after Gmail verification');
            }
          } catch (verifyError: any) {
            console.error('Profile verification failed:', verifyError);
            // Continue with signup even if verification fails, use original isProfileComplete
          }
          
          console.log('Google signup/login successful:', response.data.user);
          console.log('Profile complete:', isProfileComplete);
          
          // If profile is complete, go to dashboard; otherwise, go to profile page
          if (isProfileComplete === true) {
            console.log('Profile is complete, navigating to dashboard...');
            this.props.navigate('/customer/friends');
          } else {
            console.log('Profile is incomplete, navigating to profile page...');
            this.props.navigate('/profile');
          }
        } else {
          throw new Error('No access token found in response');
        }
      } else {
        throw new Error(response.message || 'Google signup/login failed');
      }
    } catch (error: any) {
      console.error('Google signup/login failed:', error);
      
      // Check if it's a network error (backend not running)
      if (error.message.includes('Unable to connect to server')) {
        console.log('Backend not available for Google signup/login');
        this.setState({
          errors: {
            ...this.state.errors,
            general: 'Backend server is not available. Please try again later.'
          }
        });
        return;
      }
      
      // Show error message to user
      this.setState({
        errors: {
          ...this.state.errors,
          general: error.message || 'Google signup/login failed. Please try again.'
        }
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleGoogleError = (errorInfo?: any) => {
    console.error('Google Signup Failed:', errorInfo);
    
    let errorMessage = 'Google signup was cancelled or failed. Please try again.';
    
    // Handle different error types with more specific messages
    if (errorInfo) {
      if (errorInfo.type === 'user_cancelled') {
        errorMessage = 'Sign-up was cancelled. Please try again when ready.';
      } else if (errorInfo.type === 'network_error') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (errorInfo.type === 'configuration_error') {
        errorMessage = 'Google Sign-In is not properly configured. Please contact support.';
      } else if (errorInfo.type === 'plugin_unavailable') {
        errorMessage = 'Google Sign-In is not available on this device. Please use email or phone signup.';
      } else if (errorInfo.type === 'initialization_error') {
        errorMessage = 'Failed to initialize Google Sign-In. Please try again or use email/phone signup.';
      } else if (errorInfo.message) {
        errorMessage = errorInfo.message;
      }
    }
    
    this.setState({
      errors: {
        ...this.state.errors,
        general: errorMessage
      },
      isLoading: false
    });
  };

  render() {
    const { formData, errors, isLoading } = this.state;
    
    return (
      <div className={styles.authContainer}>
        <div className={styles.authBackground}>
          <div className={styles.poolTable}>
            <div className={styles.poolBalls}>
              <div className={styles.ball}></div>
              <div className={styles.ball}></div>
              <div className={styles.ball}></div>
            </div>
          </div>
        </div>

        <div className={styles.authFormContainer}>
          <div className={styles.authForm}>
            {/* Logo */}
            <div className={styles.logo}>
              <img 
                src="/images/chatup24-logo.png" 
                alt="Chatup24" 
                className={styles.logoImage}
              />
            </div>

            {/* Header */}
            <div className={styles.authHeader}>
              <h1 className={styles.authTitle}>Join Chatup24</h1>
              <p className={styles.authSubtitle}>Create your account and start playing</p>
            </div>

            {/* Auth Method Toggle */}
            <div className={styles.authMethodToggle}>
              <button
                type="button"
                className={`${styles.toggleButton} ${this.state.authMethod === 'email' ? styles.active : ''}`}
                onClick={() => this.handleAuthMethodChange('email')}
                disabled={this.state.isLoading}
              >
                Email
              </button>
              <button
                type="button"
                className={`${styles.toggleButton} ${this.state.authMethod === 'phone' ? styles.active : ''}`}
                onClick={() => this.handleAuthMethodChange('phone')}
                disabled={this.state.isLoading}
              >
                Phone
              </button>
            </div>

            {/* Form */}
            {this.state.authMethod === 'email' ? (
            <form onSubmit={this.handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.label}>
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={this.handleInputChange}
                className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                placeholder="john@example.com"
                disabled={isLoading}
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password" className={styles.label}>
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                  onChange={this.handleInputChange}
                className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                placeholder="Create a strong password"
                disabled={isLoading}
              />
              {errors.password && <span className={styles.errorText}>{errors.password}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword" className={styles.label}>
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={this.handleInputChange}
                className={`${styles.input} ${
                  errors.confirmPassword 
                    ? styles.inputError 
                    : formData.confirmPassword && formData.password === formData.confirmPassword 
                      ? styles.inputSuccess 
                      : ''
                }`}
                placeholder="Confirm your password"
                disabled={isLoading}
              />
              {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
              {formData.confirmPassword && formData.password === formData.confirmPassword && !errors.confirmPassword && (
                <span className={styles.successText}>✓ Passwords match</span>
              )}
            </div>

            <div className={styles.formOptions}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={this.handleInputChange}
                  className={styles.checkbox}
                  disabled={isLoading}
                />
                <span className={styles.checkboxText}>
                  I agree to the{' '}
                  <Link to="/terms" className={styles.link}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className={styles.link}>
                    Privacy Policy
                  </Link>
                </span>
              </label>
            </div>

            {errors.agreeToTerms && (
              <span className={styles.errorText}>{errors.agreeToTerms}</span>
            )}

            <button
              type="submit"
              className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className={styles.loadingSpinner}></span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
          ) : (
          <div className={styles.form}>
            {!this.state.otpSent ? (
              <>
                <div className={styles.formGroup}>
                  <label htmlFor="phone" className={styles.label}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={this.handleInputChange}
                    className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                    placeholder="+1234567890"
                    disabled={isLoading}
                  />
                  {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                  <p className={styles.helperText}>
                    Enter your phone number with country code (e.g., +1234567890)
                  </p>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="password" className={styles.label}>
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={this.handleInputChange}
                    className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                    placeholder="Create a strong password"
                    disabled={isLoading}
                  />
                  {errors.password && <span className={styles.errorText}>{errors.password}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="confirmPassword" className={styles.label}>
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={this.handleInputChange}
                    className={`${styles.input} ${
                      errors.confirmPassword 
                        ? styles.inputError 
                        : formData.confirmPassword && formData.password === formData.confirmPassword 
                          ? styles.inputSuccess 
                          : ''
                    }`}
                    placeholder="Confirm your password"
                    disabled={isLoading}
                  />
                  {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
                  {formData.confirmPassword && formData.password === formData.confirmPassword && !errors.confirmPassword && (
                    <span className={styles.successText}>✓ Passwords match</span>
                  )}
                </div>

                <div className={styles.formOptions}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={this.handleInputChange}
                      className={styles.checkbox}
                      disabled={isLoading}
                    />
                    <span className={styles.checkboxText}>
                      I agree to the{' '}
                      <Link to="/terms" className={styles.link}>
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className={styles.link}>
                        Privacy Policy
                      </Link>
                    </span>
                  </label>
                </div>

                {errors.agreeToTerms && (
                  <span className={styles.errorText}>{errors.agreeToTerms}</span>
                )}

                <button
                  type="button"
                  onClick={this.handleSendOTP}
                  className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
                  disabled={isLoading || !formData.agreeToTerms}
                >
                  {isLoading ? (
                    <span className={styles.loadingSpinner}></span>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </>
            ) : (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Enter OTP
                  </label>
                  <OTPInput
                    length={6}
                    onComplete={this.handleOTPComplete}
                    onChange={this.handleOTPChange}
                    disabled={isLoading}
                    error={errors.otp}
                  />
                  {errors.otp && <span className={styles.errorText}>{errors.otp}</span>}
                </div>

                <button
                  type="button"
                  onClick={() => this.verifyOTP()}
                  className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
                  disabled={isLoading || !this.state.otpComplete}
                >
                  {isLoading ? (
                    <span className={styles.loadingSpinner}></span>
                  ) : (
                    'Verify OTP'
                  )}
                </button>

                <div className={styles.otpActions}>
                  <button
                    type="button"
                    onClick={this.handleSendOTP}
                    className={styles.resendButton}
                    disabled={isLoading || this.state.countdown > 0}
                  >
                    {this.state.countdown > 0 
                      ? `Resend OTP in ${this.state.countdown}s` 
                      : 'Resend OTP'}
                  </button>
                  <button
                    type="button"
                    onClick={() => this.setState({ otpSent: false, countdown: 0, otpComplete: false, currentOtp: '' })}
                    className={styles.changeNumberButton}
                    disabled={isLoading}
                  >
                    Change Number
                  </button>
                </div>
              </>
            )}
          </div>
          )}

          {/* reCAPTCHA Container (hidden) */}
          <div id="recaptcha-container-signup"></div>

          {/* General Error Message */}
          {errors.general && (
            <div className={styles.errorMessage}>
              {errors.general}
            </div>
          )}

          {/* Divider */}
          <div className={styles.divider}>
            <span>or</span>
          </div>

          {/* Social Login */}
          <div className={styles.socialLogin}>
            <GoogleSignIn
              onSuccess={this.handleGoogleSuccess}
              onError={this.handleGoogleError}
              useOneTap
            />
          </div>

          {/* Sign In Link */}
          <div className={styles.authFooter}>
            <p>
              Already have an account?{' '}
              <Link to="/login" className={styles.authLink}>
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
    );
  }
}

export default withNavigation(Signup);
